import { Build, ComponentInterface } from "@stencil/core"

import { DuetLanguage, DuetLanguageWithUs, DuetLocale } from "../common-types"
import { convertHtmlQuotes } from "./string-utils"

/**
 * These utilities are helpfull in getting languages and returning default strings accordingly
 *
 * Everything has been coded to rely on a reload on language change and is hooked up to th <htm lang=XX>
 *
 * @example
 * changing language on a page should result in  reload and a new lang attribute
 * <htm lang=fi>
 * -> user changes language to en
 * -> browser should reload with:
 * <htm lang=en>
 */

export interface DuetLangObject {
  fi: any
  en: any
  us?: any
  sv: any
}

export type DuetMonetaryDefaults = "$" | "€" | "£"

const localeLookUpTable: Record<DuetLanguageWithUs, DuetLocale> = {
  fi: "fi-FI",
  en: "en-GB",
  us: "en-US",
  sv: "sv-SE",
}

const localeMonetarySignifierLookUpTable: DuetLangObject = {
  fi: "€",
  en: "£",
  us: "$",
  sv: "€",
}

const defLanguageArray: string[] = ["fi", "en", "sv"]
const defaultLanguage: DuetLanguage = "fi"

export const getCurrentLanguage = (): string => {
  return document.documentElement.lang ? document.documentElement.lang.toLowerCase() : defaultLanguage
}

export const isOfTypeLanguage = (keyInput: string): keyInput is DuetLanguage => {
  return defLanguageArray.includes(keyInput)
}

const checkForLanguage = (languageString: string = getCurrentLanguage()) => {
  if (isOfTypeLanguage(languageString)) {
    return languageString
  } else {
    return defaultLanguage
  }
}

/**
 * Returns the current language as a string representation (taken from html lang attribute)
 * If language is not fi|sv|en it will default to "fi"
 * @param {string} lang=fi - "fi" | "sv" | "en"
 * @default {string} "fi"
 */
export const getLanguage = (languageString: string = getCurrentLanguage()) => checkForLanguage(languageString)

/**
 * Returns the current language and locale
 * If language is not fi|sv|en it will default to "fi"
 * special case where lang can be ALSO be "us" in addition to other types - if user decides to override local language
 * @param {string} lang="fi" - "fi" | "sv" | "en" | "us"
 * @default {object} {locale: "fi-FI", money: "€"}
 */
export const getLocale = (lang: DuetLanguageWithUs): { money: DuetMonetaryDefaults; locale: DuetLocale } => {
  const returnObj = {
    locale: localeLookUpTable[lang],
    money: localeMonetarySignifierLookUpTable[lang],
  }
  if (returnObj.locale && returnObj.money) {
    return returnObj
  }
  throw new Error(`Locale for "${lang}" not found`)
}
/**
 * Takes any json object that is passed and/or htmlencoded and converts it to proper json
 */
export const sanitizeString = (string): Record<string, any> | string => {
  const argIsString = typeof string === "string"
  if (argIsString) {
    try {
      return JSON.parse(convertHtmlQuotes(string))
    } catch (e) {
      console.log("sanitizeString received a string, that didnt parse to json object", string, e)
    }
  }
  return string
}

/**
 * Returns the current string / object from a given translation object
 * If language is not fi|sv|en it will default to "fi"
 * special case where lang can be ALSO be "us" in addition to other types - if user decides to override local language
 * @param {object} langObject - {fi: "finnish", sv: "swedish", en: "english"} | string will get JSON.parsed
 * @param {boolean} languageOverride = false - in case you want to override the actual language set in html tag
 * @default {object} {locale: "fi-FI", money: "€"}
 * @returns {(string|Object)} given example contains {fi:"something"} and language===fi this will return "something"
 */
export const getLocaleString = (langObject: DuetLangObject | string, languageOverride?: string) => {
  const obj = sanitizeString(langObject)

  if (languageOverride) {
    return obj[checkForLanguage(languageOverride)]
  } else {
    return obj[getLanguage()]
  }
}

export const formatNumber = (num: number, decimals?: number, lang: string = getCurrentLanguage()) => {
  return Intl.NumberFormat(lang, {
    style: "decimal",
    minimumFractionDigits: decimals ?? 0,
    maximumFractionDigits: decimals ?? 100,
  }).format(num)
}

/**
 * Language change observer
 *
 * Change component's language property or state when the html lang attribute changes. Can also change default labeles to new language.
 * For now the <html> element must have the data-duet-lang-observe attribute set to a truthy value for the
 * language change to happen. At a later point this will be done without requiring the data attribute.
 *
 * Usage:
 * Call in component's connectedCallback
 * connectLanguageChangeObserver(this)
 * or
 * connectLanguageChangeObserver(this, [{ prop: "labelProp", defaults: "labelPropDefaults" }])
 *
 * Call in disconnectedCallback
 * disconnectLanguageChangeObserver(this)
 */

/**
 * Component instances that have subscribed to language change.
 */
const languageChangeSubscribers: Set<ComponentInterface> = new Set()
/**
 * Label properties that should be changed when language changes.
 */
const languageChangeTargets: WeakMap<ComponentInterface, { prop: string; defaults: string }[]> = new WeakMap()

const languagaChangeObserverCallback = mutationList => {
  let oldValue = defaultLanguage
  for (const mutation of mutationList) {
    if (mutation.type === "attributes" && mutation.attributeName === "lang") {
      oldValue = mutation.oldValue
      break
    }
  }
  if (document.documentElement.dataset.duetLangObserve !== "false") {
    languageChangeSubscribers.forEach((component: ComponentInterface) => {
      if (component.language) {
        component.language = getLanguage()
      }
      if (languageChangeTargets.has(component)) {
        languageChangeTargets.get(component).forEach(target => {
          if (component[target.defaults] && component[target.prop] === component[target.defaults][oldValue]) {
            component[target.prop] = getLocaleString(component[target.defaults])
          }
        })
      }
    })
  }
}
if (!Build.isServer) {
  const languageChangeObserver = new MutationObserver(languagaChangeObserverCallback)
  languageChangeObserver.observe(document.documentElement, { attributeFilter: ["lang"], attributeOldValue: true })
}

export const connectLanguageChangeObserver = (
  component: ComponentInterface,
  targets?: { prop: string; defaults: string }[] | { prop: string; defaults: string }
) => {
  languageChangeSubscribers.add(component)
  if (targets) {
    if (!Array.isArray(targets)) {
      targets = [targets]
    }
    if (targets.some(t => !(t.prop in component) || !(t.defaults in component))) {
      console.error("connectLanguageChangeObserver: component must have target.prop and target.defaults properties")
      targets = targets.filter(t => t.prop in component && t.defaults in component)
    }
    languageChangeTargets.set(component, targets)
  }
}
export const disconnectLanguageChangeObserver = (component: ComponentInterface) => {
  languageChangeSubscribers.delete(component)
  languageChangeTargets.delete(component)
}
